import React from 'react';
import { Settings as SettingsIcon } from 'lucide-react';
import { CreateInvoice } from './CreateInvoice';
import { InvoiceHistory } from './InvoiceHistory';
import { InvoicePreviewModal } from './InvoicePreviewModal';
import { FactureEmailSettingsModal } from './FactureEmailSettingsModal';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { Segmented } from '../ui/Field';
import { useAuth } from '../../context/AuthContext';
import { moduleApi } from '../../lib/api';

/**
 * Le module Facturation (maquettes 8a et 8b, patron « liste-tableau »).
 *
 * L'en-tête est commun aux deux vues : bande d'ambiance, puis une barre de contrôles qui porte la
 * bascule Créer / Historique et les réglages d'email. C'est le même en-tête que le Planning et les
 * Prestations — seules les colonnes du tableau changent d'un module à l'autre.
 */
export const FacturesModule = () => {
    const { user } = useAuth();
    const [view, setView] = React.useState<'create' | 'history'>('create');
    const [selectedInvoice, setSelectedInvoice] = React.useState<any>(null);
    const [historyRefreshKey, setHistoryRefreshKey] = React.useState(0);
    const [autoPrintToken, setAutoPrintToken] = React.useState(0);
    const [autoDownloadToken, setAutoDownloadToken] = React.useState(0);
    const [previewInvoice, setPreviewInvoice] = React.useState<any>(null);
    const [showEmailSettings, setShowEmailSettings] = React.useState(false);
    const [counts, setCounts] = React.useState({ total: 0, unpaid: 0 });

    const openInvoicePreview = React.useCallback(async (invoice: any) => {
        // Les factures les plus anciennes ont parfois un snapshot de facturation incomplet
        // (company_name manquant) — même repli que le bouton "Télécharger" de l'historique.
        let billingSnapshot = invoice.billing_snapshot || {};
        if (!billingSnapshot?.company_name) {
            try {
                const fresh = await moduleApi.getBillingSettings();
                if (fresh?.company_name) billingSnapshot = { ...fresh, ...billingSnapshot };
            } catch { /* ignore */ }
        }
        setPreviewInvoice({ ...invoice, billing_snapshot: billingSnapshot });
    }, []);

    const countLabel = counts.total === 0
        ? 'Aucune facture'
        : `${counts.total} facture${counts.total > 1 ? 's' : ''}${counts.unpaid > 0 ? ` · ${counts.unpaid} impayée${counts.unpaid > 1 ? 's' : ''}` : ''}`;

    return (
        <div className="space-y-5">
            {/* En-tête (maquette 8a) : bande d'ambiance de 96 px comme le planning et les prestations,
                puis la barre de contrôles. L'animation ne vit que dans la bande — rien ne bouge
                au-dessus d'un tableau de montants. */}
            <header className="bg-surface border border-border rounded-2xl overflow-hidden print-hidden">
                <AmbianceBand className="h-[96px]">
                    <div className="h-[96px] px-[18px] py-[15px] flex flex-col justify-between">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-[10.5px] font-bold uppercase tracking-[.08em] text-content-muted truncate">{user?.company_name || ''}</span>
                        </div>
                        <div className="text-[21px] font-bold tracking-[-0.02em] text-content leading-none">Factures</div>
                    </div>
                </AmbianceBand>

                <div className="px-[18px] py-3.5 border-t border-border flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5 flex-wrap min-w-0">
                        {view === 'history' && (
                            <span className="text-[13px] font-semibold text-content-muted whitespace-nowrap">{countLabel}</span>
                        )}
                        <Segmented
                            ariaLabel="Vue du module"
                            value={view}
                            onChange={(v) => setView(v as 'create' | 'history')}
                            options={[{ value: 'create', label: 'Créer une facture' }, { value: 'history', label: 'Historique' }]}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowEmailSettings(true)}
                        className="h-[34px] px-3.5 inline-flex items-center gap-1.5 rounded-full border border-border bg-surface text-[12.5px] font-semibold text-content whitespace-nowrap shrink-0 transition-colors hover:border-content"
                    >
                        <SettingsIcon size={14} /> Paramètres
                    </button>
                </div>
            </header>

            {view === 'history' ? (
                <InvoiceHistory
                    refreshKey={historyRefreshKey}
                    onOpenInvoice={openInvoicePreview}
                    onCountsChange={setCounts}
                />
            ) : (
                <CreateInvoice
                    initialInvoice={selectedInvoice}
                    autoPrintToken={autoPrintToken}
                    autoDownloadToken={autoDownloadToken}
                    onInvoiceSaved={() => setHistoryRefreshKey((k) => k + 1)}
                />
            )}

            <InvoicePreviewModal invoice={previewInvoice} onClose={() => setPreviewInvoice(null)} />

            {showEmailSettings && <FactureEmailSettingsModal onClose={() => setShowEmailSettings(false)} />}
        </div>
    );
};
